import React, { useEffect, useRef, useState } from 'react';

import {
  ActionIcon,
  Alert,
  AutocompleteItem,
  Button,
  Chip,
  Divider,
  Group,
  Input,
  NumberInput,
  Paper,
  Radio,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import { IconAlertSquare, IconPlus, IconTrash } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import ClassCoachRoleAutoComplete, {
  ClassCoachRoleAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/ClassCoachRoleAutoComplete';
import ClassTypeAutoComplete, {
  ClassTypeAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/ClassTypeAutoComplete';
import LocationAutoComplete, {
  LocationAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/LocationAutoComplete';
import StaffAutoComplete, {
  StaffAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/StaffAutoComplete';
import MoneyField from 'components/MoneyField';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useBulkCreateKiloClass from 'hooks/class/useBulkCreateClass';
import useCreateKiloClass from 'hooks/class/useCreateClass';
import useKiloClassType from 'hooks/classType/useClassType';
import useCheckCalendarConflicts from 'hooks/event/useCheckCalendarConflicts';
import useCheckSessionsCalendarConflicts from 'hooks/event/useCheckSessionsCalendarConflicts';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import {
  DayAbbreviation,
  KiloClassBulkCreate,
  KiloClassTypeReservationAndCancellationSettings,
  KiloClassWrite,
} from 'types';
import { TimeUnitEnum } from 'types/enums';
import { getReservationAndCancellationSettings } from 'utils/event-json-settings.helpers';
import {
  calculateEventEndDateTime,
  createRrule,
  getDayAbbreviationFromDate,
  getDayDiff,
  reconcileBulkPartialFailure,
  shouldTrackDrawerAbandonment,
  type RecurrenceFrequency,
} from 'utils/event.helpers';
import { setMaxLateCancellationIntervalCount } from 'utils/functions';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import type { SelectedCoach } from '../EventCalendar';

interface EventSession {
  sessionId: string;
  startDate: Date;
  startTime: string;
}

interface FormValues {
  kiloClassId: string;
  sessions: EventSession[];
  duration: number;
  endDate: Date | null;
  capacity: number;
  kiloClassType: ClassTypeAutoCompleteValue;
  classCoachClassCoachRoles: {
    kiloClassCoachKiloClassCoachRoleId: string;
    coach: StaffAutoCompleteValue;
    classCoachRole: ClassCoachRoleAutoCompleteValue;
  }[];
  location: LocationAutoCompleteValue;
  rootKiloClass: string | null;
  days: DayAbbreviation[];
  waitlistLimit: number;
  useLateCancellationFee: boolean;
  useNoShowFee: boolean;
  reservationAndCancellationSettings: {
    reservationWindowOpenInterval: 'minute' | 'hour' | 'day';
    reservationWindowOpenIntervalCount: number;
    reservationWindowCloseInterval: 'minute' | 'hour' | 'day';
    reservationWindowCloseIntervalCount: number;
    allowLateCancellation: boolean;
    cancellationInterval: 'minute' | 'hour' | 'day';
    cancellationIntervalCount: number;
    cancellationFee: number;
    cancellationCreditDeduction: boolean;
    noShowFee: number;
    noShowCreditDeduction: boolean;
    waitlistMax: number;
  };
  isRecurring: boolean;
  frequency: RecurrenceFrequency;
}

const formId = 'class-create-form';

// Bulk creation is capped at 15 independent sessions per submission (PROD-104).
const MAX_SESSIONS = 15;

// Shown inline on a row the bulk endpoint failed to create. Deliberately generic:
// the backend row `detail` is a raw `str(exception)` and may carry internal or
// member-identifying text, so it is never surfaced verbatim (no-PII policy).
const ROW_CREATE_ERROR_MESSAGE = "This session couldn't be created. Please review it and try again.";

const timeUnitOptions = [
  { value: TimeUnitEnum.MINUTE, label: 'Min(s)' },
  { value: TimeUnitEnum.HOUR, label: 'Hour(s)' },
  { value: TimeUnitEnum.DAY, label: 'Day(s)' },
];

const CreateClassFormDrawer = ({
  date,
  opened,
  closeHandler,
  selectedCoach,
}: {
  date: Date;
  opened: boolean;
  closeHandler: () => void;
  selectedCoach?: SelectedCoach;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { country } = useLocalization();

  const [settingsOpened, { toggle: handleSettingsToggle }] = useDisclosure();
  // Per-session errors from a partial-failure bulk response, keyed by sessionId.
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const kiloClassMutation = useCreateKiloClass();
  const bulkKiloClassMutation = useBulkCreateKiloClass();
  const staffTracking = useStaffTracking();
  // True once a create has succeeded from this drawer — lets handleCloseAndFormReset
  // distinguish an abandonment (closed without creating) from a normal post-create close.
  const submittedRef = useRef(false);
  // Explicit engagement flag. NOT form.isDirty(): the slot-change effect re-seeds
  // `sessions` with a fresh uuid on open, so isDirty() is true before any interaction.
  const engagedRef = useRef(false);
  const markEngaged = (): void => {
    engagedRef.current = true;
  };

  const form = useForm<FormValues>({
    initialValues: {
      kiloClassId: '',
      sessions: [
        {
          sessionId: uuidv4(),
          startDate: date,
          startTime: dayjs(date).format('HH:mm').toString(),
        },
      ],
      duration: 0,
      endDate: null,
      capacity: 0,
      kiloClassType: {
        kiloClassTypeId: '',
        value: '',
        label: '',
      },
      classCoachClassCoachRoles: [
        {
          kiloClassCoachKiloClassCoachRoleId: uuidv4(),
          coach: {
            staffId: '',
            value: '',
            label: '',
          },
          classCoachRole: {
            kiloClassCoachRoleId: '',
            value: '',
            label: '',
          },
        },
      ],
      location: {
        locationId: '',
        value: '',
        label: '',
      },
      rootKiloClass: null,
      days: [getDayAbbreviationFromDate(date)],
      frequency: 'weekly',
      waitlistLimit: 0,
      useLateCancellationFee: true,
      useNoShowFee: true,
      reservationAndCancellationSettings: {
        reservationWindowOpenInterval: 'day',
        reservationWindowOpenIntervalCount: 7,
        reservationWindowCloseInterval: 'hour',
        reservationWindowCloseIntervalCount: 1,
        allowLateCancellation: true,
        cancellationInterval: 'minute',
        cancellationIntervalCount: 0,
        cancellationFee: 0,
        cancellationCreditDeduction: false,
        noShowFee: 0,
        noShowCreditDeduction: false,
        waitlistMax: 5,
      },
      isRecurring: false,
    },

    validate: {
      kiloClassType: value => (value.kiloClassTypeId ? null : 'Class type is required'),
      capacity: value => (value < 1 ? 'Capacity must be more or equal 1' : null),
      duration: value => (value < 15 ? 'Duration must be more or equal 15 mins' : null),
      sessions: {
        startDate: value => (value ? null : 'Start date is required'),
        startTime: value => (value ? null : 'Start time is required'),
      },
      // Weekly/biweekly need at least one weekday, else the rrule serializes to a
      // malformed `BYDAY=`. Chips (unlike the old disabled checkboxes) let the user
      // deselect every day, so guard it here. Monthly carries no weekdays.
      days: (value, values) =>
        values.isRecurring && values.frequency !== 'monthly' && value.length === 0
          ? 'Select at least one day'
          : null,
    },
  });

  const { setFieldValue } = form;

  const { data } = useKiloClassType(form.getInputProps('kiloClassType').value.kiloClassTypeId);

  // sessions[0] is the anchor: it drives the recurrence rule + weekday derivation,
  // and it is the single session when neither bulk nor recurring is active.
  const anchorSession = form.values.sessions[0];
  const hasValidStartDateAndTime = anchorSession?.startDate && anchorSession?.startTime;

  const fullStartDate = hasValidStartDateAndTime
    ? new Date(`${dayjs(anchorSession.startDate).format('YYYY-MM-DD')}T${anchorSession.startTime}`)
    : null;

  const formattedStartDate = fullStartDate ? dayjs(fullStartDate).toISOString() : '';
  const formattedEndDate = fullStartDate
    ? dayjs(fullStartDate)
        .add(form.values.duration || 0, 'minutes')
        .toISOString()
    : null;

  // Staff calendar-conflict check (coach availability + Google Calendar). A single
  // session uses the anchor lookup below; bulk sessions each get their own per-row
  // lookup (same logic) so every row can surface its own warning.
  const isSingleSession = !form.values.isRecurring && form.values.sessions.length === 1;
  const assignedStaffIds = form.values.classCoachClassCoachRoles
    .filter(ccccr => !!ccccr.coach.staffId)
    .map(ccccr => ccccr.coach.staffId);

  const {
    data: hasCalendarConflictsData,
    isLoading: isStaffHasCalendarConflictsLoading,
    isFetching: isStaffHasCalendarConflictsFetching,
  } = useCheckCalendarConflicts({
    staffs: assignedStaffIds,
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    enabled: isSingleSession,
  });

  // Per-row conflict check for bulk (2+ one-off sessions): the same staff lookup run
  // once per row, keyed by sessionId. Non-blocking warning, mirroring the
  // single-session alert.
  const sessionConflicts = useCheckSessionsCalendarConflicts({
    sessions: form.values.sessions.map(session => {
      const start =
        session.startDate && session.startTime
          ? new Date(`${dayjs(session.startDate).format('YYYY-MM-DD')}T${session.startTime}`)
          : null;
      return {
        sessionId: session.sessionId,
        startDate: start ? dayjs(start).toISOString() : null,
        endDate: start
          ? dayjs(start)
              .add(form.values.duration || 0, 'minutes')
              .toISOString()
          : null,
      };
    }),
    staffs: assignedStaffIds,
    enabled: !form.values.isRecurring && form.values.sessions.length > 1,
  });

  const clearRowError = (sessionId: string): void => {
    setRowErrors(prev => {
      if (!prev[sessionId]) return prev;
      const next = { ...prev };
      delete next[sessionId];
      return next;
    });
  };

  const handleCloseAndFormReset = (): void => {
    // Abandonment = the drawer is dismissed after the user engaged with it but nothing
    // was created. A post-create close sets submittedRef first, so it is excluded.
    if (shouldTrackDrawerAbandonment(submittedRef.current, engagedRef.current)) {
      staffTracking.trackEventCreateAbandoned({
        event_type_module: 'class',
        session_count: form.values.sessions.length,
        is_recurring: form.values.isRecurring,
      });
    }
    closeHandler();
    form.reset();
    setRowErrors({});
  };

  const handleSessionDateChange = (index: number, startDate: Date | null): void => {
    markEngaged();
    form.setFieldValue(`sessions.${index}.startDate`, startDate);
    // The anchor session's weekday feeds the recurrence weekday chips.
    if (index === 0 && startDate) {
      form.setFieldValue('days', [getDayAbbreviationFromDate(startDate)]);
    }
    clearRowError(form.values.sessions[index].sessionId);
  };

  const handleSessionTimeChange = (index: number, event: React.ChangeEvent<HTMLInputElement>): void => {
    markEngaged();
    form.setFieldValue(`sessions.${index}.startTime`, event.currentTarget.value);
    clearRowError(form.values.sessions[index].sessionId);
  };

  const handleAddSession = (): void => {
    if (form.values.sessions.length >= MAX_SESSIONS) return;
    markEngaged();
    // Default a new row to the day after the last session (same time). Bulk mode has
    // no conflict check, so cloning the anchor's exact date/time would let the user
    // create two identical events with no warning; +1 day avoids that footgun.
    const lastSession = form.values.sessions[form.values.sessions.length - 1];
    form.insertListItem('sessions', {
      sessionId: uuidv4(),
      startDate: dayjs(lastSession.startDate).add(1, 'day').toDate(),
      startTime: lastSession.startTime,
    });
    staffTracking.trackEventSessionRowChanged({
      event_type_module: 'class',
      action: 'added',
      session_count: form.values.sessions.length + 1,
    });
  };

  const handleRemoveSession = (index: number): void => {
    markEngaged();
    const removed = form.values.sessions[index];
    form.removeListItem('sessions', index);
    if (removed) clearRowError(removed.sessionId);
    staffTracking.trackEventSessionRowChanged({
      event_type_module: 'class',
      action: 'removed',
      session_count: form.values.sessions.length - 1,
    });
  };

  const handleAddCoach = (): void => {
    markEngaged();
    form.insertListItem('classCoachClassCoachRoles', {
      kiloClassCoachKiloClassCoachRoleId: uuidv4(),
      coach: {
        staffId: '',
        value: '',
        label: '',
      },
      classCoachRole: {
        kiloClassCoachRoleId: '',
        value: '',
        label: '',
      },
    });
  };

  const handleCoachChange = (item: AutocompleteItem | null, index: number): void => {
    markEngaged();
    const coachValue = {
      staffId: item ? item.staffId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue(`classCoachClassCoachRoles.${index}.coach`, coachValue);
  };

  const handleClassCoachRoleChange = (item: AutocompleteItem | null, index: number): void => {
    markEngaged();
    const selectedClassCoachRole = {
      kiloClassCoachRoleId: item ? item.kiloClassCoachRoleId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue(`classCoachClassCoachRoles.${index}.classCoachRole`, selectedClassCoachRole);
  };

  const handleLocationChange = (item: LocationAutoCompleteValue | null): void => {
    markEngaged();
    const selectedLocation = {
      locationId: item ? item.locationId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('location', selectedLocation);
  };

  const handleClassTypeChange = (item: ClassTypeAutoCompleteValue | null): void => {
    markEngaged();
    const selectedKiloClassType = {
      kiloClassTypeId: item ? item.kiloClassTypeId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('kiloClassType', selectedKiloClassType);
  };

  const buildReservationAndCancellationSettings = (
    values: FormValues,
  ): KiloClassTypeReservationAndCancellationSettings => ({
    reservationWindowOpenInterval: values.reservationAndCancellationSettings.reservationWindowOpenInterval,
    reservationWindowOpenIntervalCount:
      values.reservationAndCancellationSettings.reservationWindowOpenIntervalCount,
    reservationWindowCloseInterval: values.reservationAndCancellationSettings.reservationWindowCloseInterval,
    reservationWindowCloseIntervalCount:
      values.reservationAndCancellationSettings.reservationWindowCloseIntervalCount,
    allowLateCancellation: values.reservationAndCancellationSettings.allowLateCancellation,
    cancellationInterval: values.reservationAndCancellationSettings.cancellationInterval,
    cancellationIntervalCount: values.reservationAndCancellationSettings.cancellationIntervalCount,
    cancellationFee:
      values.useLateCancellationFee && values.reservationAndCancellationSettings.allowLateCancellation
        ? values.reservationAndCancellationSettings.cancellationFee
        : 0,
    cancellationCreditDeduction: values.reservationAndCancellationSettings.allowLateCancellation
      ? values.reservationAndCancellationSettings.cancellationCreditDeduction
      : false,
    noShowFee: values.useNoShowFee ? values.reservationAndCancellationSettings.noShowFee : 0,
    noShowCreditDeduction: values.reservationAndCancellationSettings.noShowCreditDeduction,
    waitlistMax: values.waitlistLimit,
  });

  const submitBulk = (values: FormValues): void => {
    const { errorNotification, successNotification, infoNotification } = notificationHandler();
    const payload: KiloClassBulkCreate = {
      duration: values.duration,
      capacity: values.capacity,
      kiloClassType: values.kiloClassType.kiloClassTypeId,
      location: values.location?.locationId || null,
      waitlistLimit: values.waitlistLimit,
      reservationAndCancellationSettings: buildReservationAndCancellationSettings(values),
      kiloClassCoachKiloClassCoachRoles: values.classCoachClassCoachRoles.map(ccccr => ({
        kiloClassCoachRole: ccccr.classCoachRole.kiloClassCoachRoleId,
        coach: ccccr.coach.staffId,
      })),
      rows: values.sessions.map(session => ({
        startDate: dayjs(session.startDate).format('YYYY-MM-DD'),
        startTime: session.startTime,
      })),
    };

    bulkKiloClassMutation.mutate(payload, {
      onSuccess: response => {
        const { created, errors } = response.data;
        staffTracking.trackEventBulkCreated({
          event_type_module: 'class',
          event_type: values.kiloClassType.label,
          event_type_id: values.kiloClassType.kiloClassTypeId,
          session_count: values.sessions.length,
          created_count: created.length,
          failure_count: errors.length,
          created_ids: created.map(kiloClass => kiloClass.kiloClassId),
        });
        // Any successful row means the drawer's close is not an abandonment.
        if (created.length) submittedRef.current = true;
        if (!errors.length) {
          successNotification(`${created.length} classes created successfully`);
          handleCloseAndFormReset();
          return;
        }

        // Partial failure: drop the sessions that succeeded, keep the failed ones
        // in the form with a generic inline error so the user can retry only those.
        // The backend `detail` is intentionally not shown verbatim (see
        // ROW_CREATE_ERROR_MESSAGE).
        const sentSessions = values.sessions;
        const {
          survivingSessions,
          rowErrors: nextRowErrors,
          allFailed,
        } = reconcileBulkPartialFailure(sentSessions, errors, created.length, ROW_CREATE_ERROR_MESSAGE);

        form.setFieldValue('sessions', survivingSessions);
        setRowErrors(nextRowErrors);

        // Every row failed → surface it as an error, not a neutral info toast.
        const notify = allFailed ? errorNotification : infoNotification;
        notify(
          allFailed
            ? `Could not create ${sentSessions.length} classes`
            : `${created.length} of ${sentSessions.length} classes created`,
          'Review the highlighted sessions below and try again.',
        );
      },
      onError: error => {
        errorNotification('Bulk class creation failed', error.response?.data.message);
      },
    });
  };

  const submitSingle = (values: FormValues): void => {
    const { errorNotification, successNotification } = notificationHandler();
    const rrule = values.isRecurring
      ? createRrule(anchorSession.startDate, values.endDate, values.days, values.frequency)
      : null;

    const payload: KiloClassWrite = {
      kiloClassId: uuidv4(),
      rootKiloClass: null,
      kiloClassType: values.kiloClassType.kiloClassTypeId,
      kiloClassCoachKiloClassCoachRoles: values.classCoachClassCoachRoles.map(ccccr => ({
        kiloClassCoachRole: ccccr.classCoachRole.kiloClassCoachRoleId,
        coach: ccccr.coach.staffId,
      })),
      location: values.location?.locationId || null,
      capacity: values.capacity,
      duration: values.duration,
      startDate: dayjs(anchorSession.startDate).format('YYYY-MM-DD'),
      startTime: anchorSession.startTime,
      endDate: values.endDate ? dayjs(values.endDate).format('YYYY-MM-DD') : null,
      rrule,
      waitlistLimit: values.waitlistLimit,
      reservationAndCancellationSettings: buildReservationAndCancellationSettings(values),
      editThisEventOnly: false,
      sendNotification: true,
    };
    kiloClassMutation.mutate(payload, {
      onSuccess: () => {
        successNotification('Class created successfully');
        // TRACK: send staff_event_created event
        staffTracking.trackEventCreate(
          payload.kiloClassId,
          values.kiloClassType.label,
          values.kiloClassType.kiloClassTypeId || '',
          values.capacity,
          anchorSession.startTime,
          values.location?.label || '',
          values.duration,
          values.isRecurring,
          'class',
        );
        submittedRef.current = true;
        handleCloseAndFormReset();
      },
      onError: error => {
        errorNotification('Creating class failed', error.response?.data.message);
      },
    });
  };

  const handleSubmit = (values: FormValues): void => {
    if (kiloClassMutation.isLoading || bulkKiloClassMutation.isLoading) return;
    // Multiple one-off sessions go to the bulk endpoint; a lone session (or a
    // recurring series) keeps the single-create path so its notification handling
    // stays unchanged.
    if (!values.isRecurring && values.sessions.length > 1) {
      submitBulk(values);
    } else {
      submitSingle(values);
    }
  };

  const handleValidationError = (errors: typeof form.errors): void => {
    // Only the field keys that failed — never the values (no-PII). Fired when a
    // submit is blocked by validation (validation-error frequency analytics).
    staffTracking.trackEventCreateValidationFailed({
      event_type_module: 'class',
      error_fields: Object.keys(errors),
      session_count: form.values.sessions.length,
      is_recurring: form.values.isRecurring,
    });
  };

  const isSubmitButtonLoading =
    kiloClassMutation.isLoading ||
    bulkKiloClassMutation.isLoading ||
    isStaffHasCalendarConflictsLoading ||
    isStaffHasCalendarConflictsFetching;

  const sessionCount = form.values.sessions.length;
  const submitLabel = form.values.isRecurring
    ? 'Save'
    : sessionCount > 1
    ? `Create ${sessionCount} classes`
    : 'Create class';

  useShallowEffect(() => {
    if (data?.data) {
      if (data.data.location) {
        form.setFieldValue('location', {
          locationId: data.data.location.locationId,
          value: data.data.location.locationId,
          label: data.data.location.name,
        });
      }
      form.setFieldValue('capacity', data.data.capacity);
      form.setFieldValue('duration', data.data.duration);

      const reservationAndCancellationSettings = getReservationAndCancellationSettings(
        data.data.reservationAndCancellationSettings,
      );
      form.setFieldValue('useLateCancellationFee', reservationAndCancellationSettings.cancellationFee > 0);
      form.setFieldValue('useNoShowFee', reservationAndCancellationSettings.noShowFee > 0);
      form.setFieldValue('reservationAndCancellationSettings', reservationAndCancellationSettings);

      form.setFieldValue('waitlistLimit', data.data.waitlistLimit || 0);
    }
  }, [data?.data]);

  useEffect(() => {
    // A new calendar slot was picked: collapse the repeater back to a single anchor
    // session at that slot. Resetting only sessions[0] would leave stale extra rows
    // (and their inline errors) from a prior interaction pointing at the old date.
    setFieldValue('sessions', [
      { sessionId: uuidv4(), startDate: date, startTime: dayjs(date).format('HH:mm').toString() },
    ]);
    setFieldValue('days', [getDayAbbreviationFromDate(date)]);
    setRowErrors({});
  }, [date, setFieldValue]);

  useEffect(() => {
    const staffId = selectedCoach?.staffId ?? '';
    setFieldValue('classCoachClassCoachRoles.0.coach', {
      staffId,
      value: staffId,
      label: selectedCoach?.name ?? '',
    });
  }, [selectedCoach, setFieldValue]);

  // Reset the submitted flag each time the drawer opens so abandonment is tracked
  // per open/close cycle.
  useEffect(() => {
    if (opened) {
      submittedRef.current = false;
      engagedRef.current = false;
    }
  }, [opened]);

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndFormReset}
      size='lg'
      title={<Title order={3}>Create Class</Title>}
      closeOnClickOutside
      footer={
        <Group position='right'>
          <div>
            <Button variant='subtle' onClick={handleCloseAndFormReset} mr='sm'>
              Cancel
            </Button>
            <Button form={formId} type='submit' loading={isSubmitButtonLoading} data-cy='saveClass'>
              {submitLabel}
            </Button>
          </div>
        </Group>
      }
    >
      <form id={formId} onSubmit={form.onSubmit(handleSubmit, handleValidationError)}>
        <Stack spacing='xs'>
          <Group grow align='top'>
            <ClassTypeAutoComplete
              label='Class Type'
              placeholder='Select a class type'
              value={form.values.kiloClassType}
              error={form.errors.kiloClassType}
              onChange={handleClassTypeChange}
              required
              onClearValue={() => form.reset()}
              data-cy='classTypeInput'
              wrapperProps={{ 'data-cy': 'classTypeInputWrapper' }}
            />
            <div />
          </Group>

          <Paper withBorder p='sm' bg='gray.0'>
            <Stack spacing='sm'>
              {!form.values.classCoachClassCoachRoles.length && (
                <Text size='sm' color='dimmed'>
                  No Coach
                </Text>
              )}
              {form.values.classCoachClassCoachRoles.map((ccccr, i) => (
                <Group
                  key={ccccr.kiloClassCoachKiloClassCoachRoleId}
                  position='apart'
                  align='flex-start'
                  noWrap
                >
                  <StaffAutoComplete
                    label='Coach'
                    placeholder='Select a coach'
                    {...form.getInputProps(`classCoachClassCoachRoles.${i}.coach`)}
                    onChange={value => handleCoachChange(value, i)}
                    required
                    disinclude={form.values.classCoachClassCoachRoles.map(c => c.coach.staffId)}
                    isCoach
                    data-cy={`classCoachAutoComplete${i}`}
                  />
                  <ClassCoachRoleAutoComplete
                    label='Role'
                    placeholder='Select a role'
                    {...form.getInputProps(`classCoachClassCoachRoles.${i}.classCoachRole`)}
                    onChange={value => handleClassCoachRoleChange(value, i)}
                    required
                    data-cy={`classCoachRoleAutoComplete${i}`}
                  />
                  <ActionIcon
                    mt='xl'
                    size='lg'
                    color='red'
                    onClick={() => form.removeListItem('classCoachClassCoachRoles', i)}
                  >
                    <IconTrash size={theme.fontSizes.lg} data-cy='createClassModalRemoveCoach' />
                  </ActionIcon>
                </Group>
              ))}
              <ActionIcon variant='transparent' color='kilo-blue' onClick={handleAddCoach}>
                <IconPlus />
              </ActionIcon>
            </Stack>
          </Paper>

          <Group grow align='top'>
            <LocationAutoComplete
              label='Location'
              placeholder='Select a location'
              value={form.values.location}
              onChange={handleLocationChange}
              error={form.errors.location}
              data-cy='locationInput'
            />
            <NumberInput
              {...form.getInputProps('capacity')}
              label='Capacity'
              placeholder='Enter class capacity'
              required
              min={1}
            />
          </Group>

          <Divider mt='xs' mx='-xl' />

          <Group grow align='top'>
            <NumberInput
              {...form.getInputProps('duration')}
              label='Duration in minutes'
              placeholder='Enter duration'
              required
              min={15}
              max={60 * 24 * 7}
            />
            <div />
          </Group>

          <SegmentedControl
            fullWidth
            data-test='session-type'
            value={form.values.isRecurring ? 'recurring' : 'single'}
            onChange={value => {
              markEngaged();
              const recurring = value === 'recurring';
              form.setFieldValue('isRecurring', recurring);
              if (!recurring) {
                form.setFieldValue('endDate', null);
              }
            }}
            data={[
              { value: 'single', label: 'Single Class' },
              { value: 'recurring', label: 'Recurring Event' },
            ]}
          />

          <Stack spacing='sm'>
            {form.values.sessions.map((session, index) => {
              // In recurring mode only the anchor session is used.
              if (form.values.isRecurring && index > 0) return null;

              const sessionEnd =
                form.values.duration && session.startTime
                  ? calculateEventEndDateTime(form.values.duration, session.startTime, session.startDate)
                  : null;
              const dayDiff = sessionEnd ? getDayDiff(dayjs(session.startDate), sessionEnd) : '';
              const rowError = rowErrors[session.sessionId];
              const hasConflict = sessionConflicts[session.sessionId];
              const canRemove = !form.values.isRecurring && form.values.sessions.length > 1;

              return (
                <Stack key={session.sessionId} spacing={4}>
                  <Group align='top' noWrap spacing='xs'>
                    <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]} style={{ flex: 1 }}>
                      <DateInput
                        {...form.getInputProps(`sessions.${index}.startDate`)}
                        label={index === 0 ? 'Start Date' : `Session ${index + 1} date`}
                        locale='us'
                        onChange={value => handleSessionDateChange(index, value)}
                        required
                        data-test={`session-${index}-date`}
                      />
                      <Group spacing='xs' grow>
                        <TimeInput
                          label='From'
                          {...form.getInputProps(`sessions.${index}.startTime`)}
                          onChange={event => handleSessionTimeChange(index, event)}
                          required
                          data-test={`session-${index}-time`}
                        />
                        <TimeInput
                          label={
                            <Group position='apart'>
                              <div>To</div>
                              <Text color='red'>{dayDiff}</Text>
                            </Group>
                          }
                          value={sessionEnd ? sessionEnd.format('HH:mm') : ''}
                          readOnly
                          disabled
                        />
                      </Group>
                    </SimpleGrid>
                    {canRemove && (
                      <ActionIcon
                        mt='xl'
                        size='lg'
                        color='red'
                        onClick={() => handleRemoveSession(index)}
                        data-test={`remove-session-${index}`}
                      >
                        <IconTrash size={theme.fontSizes.lg} />
                      </ActionIcon>
                    )}
                  </Group>
                  {rowError && (
                    <Text size='xs' color='red'>
                      {rowError}
                    </Text>
                  )}
                  {hasConflict && (
                    <Alert
                      variant='light'
                      color='yellow'
                      icon={<IconAlertSquare size={theme.fontSizes.md} />}
                      py={4}
                      px='xs'
                      data-test={`session-${index}-conflict`}
                    >
                      <Text size='xs'>
                        One or more assigned coaches have a scheduling conflict with this event. Do you still
                        want to proceed? Save to confirm the change.
                      </Text>
                    </Alert>
                  )}
                </Stack>
              );
            })}

            {!form.values.isRecurring && (
              <Group position='apart'>
                <Button
                  variant='subtle'
                  leftIcon={<IconPlus size={theme.fontSizes.md} />}
                  onClick={handleAddSession}
                  disabled={sessionCount >= MAX_SESSIONS}
                  data-test='add-session'
                >
                  Add session
                </Button>
                <Text size='xs' color='dimmed'>
                  {sessionCount} of {MAX_SESSIONS} sessions
                </Text>
              </Group>
            )}
          </Stack>

          {isSingleSession && !isSubmitButtonLoading && hasCalendarConflictsData?.hasConflict && (
            <Alert variant='light' color='yellow' icon={<IconAlertSquare />}>
              One or more assigned coaches have a scheduling conflict with this event. Do you still want to
              proceed? Save to confirm the change.
            </Alert>
          )}

          {form.values.isRecurring && (
            <Stack pt='xs'>
              <Radio.Group
                label='Recurrence Type'
                data-test='recurrence-type'
                {...form.getInputProps('frequency')}
              >
                <Group grow align='stretch' pt='xs'>
                  {(
                    [
                      { value: 'weekly', title: 'Weekly', subtitle: 'Every 7 days' },
                      { value: 'biweekly', title: 'Biweekly', subtitle: 'Every 14 days' },
                      { value: 'monthly', title: 'Monthly', subtitle: 'Same date each calendar month' },
                    ] as const
                  ).map(option => (
                    <Paper key={option.value} withBorder radius='md' p='md'>
                      <Radio
                        value={option.value}
                        data-test={`recurrence-${option.value}`}
                        labelPosition='left'
                        styles={{ body: { width: '100%', justifyContent: 'space-between' } }}
                        label={
                          <Stack spacing={2}>
                            <Text fw={600}>{option.title}</Text>
                            <Text size='sm' color='dimmed'>
                              {option.subtitle}
                            </Text>
                          </Stack>
                        }
                      />
                    </Paper>
                  ))}
                </Group>
              </Radio.Group>

              {form.values.frequency !== 'monthly' && (
                <Stack spacing={4}>
                  <Text size='sm' fw={500}>
                    Days of the week
                  </Text>
                  <Chip.Group multiple {...form.getInputProps('days')}>
                    <Group spacing='sm'>
                      {(
                        [
                          ['SU', 'Sun', 'sunday'],
                          ['MO', 'Mon', 'monday'],
                          ['TU', 'Tue', 'tuesday'],
                          ['WE', 'Wed', 'wednesday'],
                          ['TH', 'Thu', 'thursday'],
                          ['FR', 'Fri', 'friday'],
                          ['SA', 'Sat', 'saturday'],
                        ] as const
                      ).map(([value, label, testId]) => (
                        <Chip key={value} value={value} data-test={testId}>
                          {label}
                        </Chip>
                      ))}
                    </Group>
                  </Chip.Group>
                  {form.errors.days && (
                    <Text size='xs' color='red'>
                      {form.errors.days}
                    </Text>
                  )}
                </Stack>
              )}

              <Group grow align='top'>
                <DateInput
                  {...form.getInputProps('endDate')}
                  label='End date'
                  placeholder='Never'
                  data-test='endDate'
                  minDate={anchorSession?.startDate}
                  clearable
                />
                <div />
              </Group>
            </Stack>
          )}
          <Divider mt='lg' mb={0} mx='-xl' />

          <ExpandableSection
            title='Additional Settings'
            opened={settingsOpened}
            handleToggle={handleSettingsToggle}
          >
            <Stack spacing='sm' px='md'>
              <Input.Wrapper label='Waitlist capacity'>
                <SimpleGrid cols={2}>
                  <NumberInput min={0} {...form.getInputProps('waitlistLimit')} required />
                </SimpleGrid>
              </Input.Wrapper>
              <Input.Wrapper label='The amount of time before a class when reservations will be accepted'>
                <SimpleGrid cols={2}>
                  <NumberInput
                    min={0}
                    {...form.getInputProps(
                      'reservationAndCancellationSettings.reservationWindowOpenIntervalCount',
                    )}
                    required
                  />
                  <Select
                    data={timeUnitOptions}
                    {...form.getInputProps(
                      'reservationAndCancellationSettings.reservationWindowOpenInterval',
                    )}
                  />
                </SimpleGrid>
              </Input.Wrapper>
              <Input.Wrapper label='The amount of time before a class when reservations will no longer be accepted'>
                <SimpleGrid cols={2}>
                  <NumberInput
                    min={0}
                    {...form.getInputProps(
                      'reservationAndCancellationSettings.reservationWindowCloseIntervalCount',
                    )}
                    required
                  />
                  <Select
                    data={timeUnitOptions}
                    {...form.getInputProps(
                      'reservationAndCancellationSettings.reservationWindowCloseInterval',
                    )}
                  />
                </SimpleGrid>
              </Input.Wrapper>
              <Input.Wrapper>
                <SimpleGrid cols={1}>
                  <Switch
                    my='xs'
                    label='Allow late cancellations'
                    checked={form.values?.reservationAndCancellationSettings?.allowLateCancellation || false}
                    {...form.getInputProps('reservationAndCancellationSettings.allowLateCancellation')}
                  />
                </SimpleGrid>
              </Input.Wrapper>
              <Input.Wrapper
                label={
                  form.values.reservationAndCancellationSettings.allowLateCancellation
                    ? 'Time before the start of a class when a cancellation is considered late'
                    : 'Time before the start of a class when cancellations are no longer allowed'
                }
              >
                <SimpleGrid cols={2}>
                  <NumberInput
                    min={0}
                    max={setMaxLateCancellationIntervalCount(
                      form.values.reservationAndCancellationSettings.cancellationInterval,
                    )}
                    {...form.getInputProps('reservationAndCancellationSettings.cancellationIntervalCount')}
                    required
                  />
                  <Select
                    data={timeUnitOptions}
                    {...form.getInputProps('reservationAndCancellationSettings.cancellationInterval')}
                  />
                </SimpleGrid>
              </Input.Wrapper>

              {form.values.reservationAndCancellationSettings.allowLateCancellation && (
                <>
                  <Group noWrap>
                    <Switch
                      checked={form.values.useLateCancellationFee}
                      {...form.getInputProps('useLateCancellationFee')}
                    />
                    <Text size='sm'>Auto-Charge Fee for late cancellations</Text>
                    {form.values.useLateCancellationFee && (
                      <MoneyField
                        country={country}
                        min={0}
                        {...form.getInputProps('reservationAndCancellationSettings.cancellationFee')}
                      />
                    )}
                  </Group>

                  <Input.Wrapper>
                    <SimpleGrid cols={1}>
                      <Switch
                        my='xs'
                        label='Deduction of credit for late cancellation'
                        checked={
                          form.values?.reservationAndCancellationSettings?.cancellationCreditDeduction ||
                          false
                        }
                        {...form.getInputProps(
                          'reservationAndCancellationSettings.cancellationCreditDeduction',
                        )}
                      />
                    </SimpleGrid>
                  </Input.Wrapper>
                </>
              )}
              <Divider />
              <Group noWrap>
                <Switch checked={form.values.useNoShowFee} {...form.getInputProps('useNoShowFee')} />
                <Text size='sm'>Auto-Charge Fee for No Shows</Text>
                {form.values.useNoShowFee && (
                  <MoneyField
                    country={country}
                    min={0}
                    {...form.getInputProps('reservationAndCancellationSettings.noShowFee')}
                  />
                )}
              </Group>
              <Input.Wrapper>
                <SimpleGrid cols={1}>
                  <Switch
                    my='xs'
                    label='Deduction of credit for No Shows'
                    checked={form.values?.reservationAndCancellationSettings?.noShowCreditDeduction || false}
                    {...form.getInputProps('reservationAndCancellationSettings.noShowCreditDeduction')}
                  />
                </SimpleGrid>
              </Input.Wrapper>
            </Stack>
          </ExpandableSection>
        </Stack>
      </form>
    </Drawer>
  );
};

export default CreateClassFormDrawer;
